'use client';

import { Check, Share, UserPlus } from 'lucide-react';
import { Button } from '../Button/Button';
import { usePathname, useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { createClient } from '@/utils/supabase/client';
import { copyToClipboard } from '@/utils/clipboard';
import { toast } from '@/components/ui/Toasts/use-toast';
import { useState } from 'react';

const FollowShare = ({
  profileId,
  userId,
  isFollowing: initialIsFollowing,
}: {
  profileId: string;
  userId?: string;
  isFollowing: boolean;
}) => {
  const router = useRouter();
  const pathname = usePathname();
  const t = useTranslations('profile');
  const supabase = createClient();
  const [isFollowing, setIsFollowing] = useState(initialIsFollowing);

  const copyUrl = async () => {
    const url = `${window.location.origin}${pathname}`;
    try {
      await copyToClipboard(url);
      toast({
        title: 'URL copied',
        description: 'The URL has been copied to your clipboard',
      });
    } catch (error) {
      console.error('Failed to copy URL:', error);
      toast({
        title: 'Failed to copy URL',
        description: 'An error occurred while trying to copy the URL to your clipboard',
        variant: 'destructive',
      });
    }
  };

  const follow = async () => {
    if (!userId) {
      // Not logged in
      router.push(`signin/email`);
      return;
    }
    const { error } = await supabase.from('follows').insert({
      follower_id: userId,
      followed_id: profileId,
    });

    if (error) {
      console.error('Failed to follow:', error);
      toast({
        title: 'Failed to follow',
        description: 'An error occurred while trying to follow this profile',
        variant: 'destructive',
      });
    } else {
      toast({ title: 'Followed', description: 'You are now following this profile' });
      setIsFollowing(true);
    }
  };

  const unfollow = async () => {
    if (!userId) {
      // Not logged in
      router.push(`signin/email`);
      return;
    }
    const { error } = await supabase
      .from('follows')
      .delete()
      .eq('follower_id', userId)
      .eq('followed_id', profileId);

    if (error) {
      console.error('Failed to unfollow:', error);
      toast({
        title: 'Failed to unfollow',
        description: 'An error occurred while trying to unfollow this profile',
        variant: 'destructive',
      });
    } else {
      toast({ title: 'Unfollowed', description: 'You are no longer following this profile' });
      setIsFollowing(false);
    }
  };

  return (
    <div className="flex flex-row gap-4">
      {isFollowing ? (
        <Button
          onClick={unfollow}
          style={{ width: '100%', paddingLeft: '1rem', paddingRight: '1rem' }}
          className="py-2"
          isSecondary
          tracking={{
            event: 'user.unfollowed',
          }}
        >
          <Check size={20} />
          {t('following')}
        </Button>
      ) : (
        <Button
          onClick={follow}
          style={{ width: '100%', paddingLeft: '1rem', paddingRight: '1rem' }}
          className="py-2"
          isPrimary
          tracking={{
            event: 'user.followed',
          }}
        >
          <UserPlus size={20} />
          {t('follow')}
        </Button>
      )}
      <Button
        onClick={copyUrl}
        style={{ width: '100%', paddingLeft: '1rem', paddingRight: '1rem' }}
        isSecondary
        tracking={{
          event: 'profile.shared',
        }}
      >
        <Share size={20} />
        {t('share')}
      </Button>
    </div>
  );
};

export default FollowShare;
